import { useLayoutEffect, useRef, useState } from "react";
import type { HTMLAttributes, PointerEvent as ReactPointerEvent, ReactNode } from "react";

export interface SegmentedControlOption<Value extends string> {
  value: Value;
  label: ReactNode;
  disabled?: boolean;
}

interface SegmentedControlProps<Value extends string>
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  options: readonly SegmentedControlOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  size?: "sm" | "md" | "kpi";
  fullWidth?: boolean;
  optionClassName?: string;
}

const OPTION_SIZES = {
  sm: "px-3 py-1 text-[10px]",
  md: "px-4 py-1.5 text-[11px]",
  // Match ranking filters sit directly below KpiBadge rows. Keep their
  // options on the same 12px / py-1.5 geometry as those score pills.
  kpi: "px-3 py-1.5 text-[12px]",
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

interface IndicatorRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function SegmentedControl<Value extends string>({
  options,
  value,
  onChange,
  size = "md",
  fullWidth = false,
  className = "",
  optionClassName = "",
  ...props
}: SegmentedControlProps<Value>) {
  const groupRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<Map<Value, HTMLButtonElement>>(new Map());
  // Position of the sliding indicator, measured from the active option's box.
  // null until first measured, so the indicator never flashes at 0,0 and never
  // animates in from the corner. Measured in useLayoutEffect (before paint) so
  // the indicator's first appearance is already at the right spot.
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
  // While the user drags the indicator, drag overrides its measured x so it
  // follows the pointer 1:1 (with the transition suppressed). Width is frozen
  // to the grabbed size so the pill keeps shape mid-drag even when options
  // differ in width. On release drag clears and the indicator snaps to the
  // committed option's box via the CSS transition.
  const [drag, setDrag] = useState<{ x: number; w: number } | null>(null);
  // Geometry snapshot taken at drag start: each enabled option's centre and the
  // indicator's travel bounds. Positions are stable during a drag (only the
  // absolutely-positioned indicator moves), so we read layout once here instead
  // of on every pointermove. Bounds use the enabled extremes so the indicator
  // can't be parked over a disabled edge option.
  const dragGeom = useRef<{
    minX: number;
    maxX: number;
    stops: Array<{ value: Value; center: number }>;
  } | null>(null);

  // Stable key over the option set. Re-measuring must react to options being
  // added/removed/reordered (which moves the active option even when the group
  // box doesn't change, e.g. fullWidth), but depending on `options` directly
  // would re-run every render since call sites pass a fresh array literal.
  const optionsKey = options.map((option) => option.value).join("|");

  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    const measure = () => {
      const active = buttonsRef.current.get(value);
      // No matching option (e.g. a mixed/none aggregate value): hide the pill
      // instead of leaving it parked on the previously-active option.
      if (!active) {
        setIndicator(null);
        return;
      }
      const next = {
        x: active.offsetLeft,
        y: active.offsetTop,
        w: active.offsetWidth,
        h: active.offsetHeight,
      };
      // Bail out when nothing actually moved, so a parent re-render that re-runs
      // this effect doesn't churn out an identical new object (extra render).
      setIndicator((prev) =>
        prev && prev.x === next.x && prev.y === next.y && prev.w === next.w && prev.h === next.h
          ? prev
          : next,
      );
    };
    measure();
    // Re-measure on layout changes (fullWidth controls reflow with the sidebar,
    // label width can change on locale switch).
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    return () => observer.disconnect();
  }, [value, optionsKey]);

  const dragging = drag !== null;

  function handlePointerDown(event: ReactPointerEvent<HTMLSpanElement>) {
    if (options.length < 2 || !indicator) return;
    const enabled = options
      .filter((option) => !option.disabled)
      .map((option) => ({ value: option.value, button: buttonsRef.current.get(option.value) }))
      .filter((entry): entry is { value: Value; button: HTMLButtonElement } => Boolean(entry.button));
    const first = enabled[0]?.button;
    const last = enabled[enabled.length - 1]?.button;
    if (!first || !last) return;
    dragGeom.current = {
      minX: first.offsetLeft,
      maxX: last.offsetLeft + last.offsetWidth - indicator.w,
      stops: enabled.map((entry) => ({
        value: entry.value,
        center: entry.button.offsetLeft + entry.button.offsetWidth / 2,
      })),
    };
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ x: indicator.x, w: indicator.w });
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLSpanElement>) {
    const geom = dragGeom.current;
    const group = groupRef.current;
    if (!drag || !geom || !group) return;
    const width = drag.w;
    const pointerX = event.clientX - group.getBoundingClientRect().left;
    const x = Math.max(geom.minX, Math.min(geom.maxX, pointerX - width / 2));
    setDrag({ x, w: width });

    const centerX = x + width / 2;
    let nearest: Value | null = null;
    let best = Infinity;
    for (const stop of geom.stops) {
      const distance = Math.abs(stop.center - centerX);
      if (distance < best) {
        best = distance;
        nearest = stop.value;
      }
    }
    if (nearest && nearest !== value) onChange(nearest);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLSpanElement>) {
    if (!drag) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragGeom.current = null;
    setDrag(null);
  }

  return (
    <div
      ref={groupRef}
      role="group"
      className={cx("smycles-pill-group", fullWidth && "w-full", dragging && "select-none", className)}
      {...props}
    >
      {indicator && (
        <span
          aria-hidden
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className={cx("smycles-pill-indicator", dragging && "smycles-pill-indicator--dragging")}
          style={{
            transform: `translate(${drag?.x ?? indicator.x}px, ${indicator.y}px)`,
            width: drag?.w ?? indicator.w,
            height: indicator.h,
          }}
        />
      )}
      {options.map((option) => (
        <button
          key={option.value}
          ref={(el) => {
            if (el) buttonsRef.current.set(option.value, el);
            else buttonsRef.current.delete(option.value);
          }}
          type="button"
          aria-pressed={option.value === value}
          disabled={option.disabled}
          onClick={() => onChange(option.value)}
          className={cx(
            "smycles-pill",
            OPTION_SIZES[size],
            fullWidth && "flex-1",
            optionClassName,
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
